import type { ReactNode } from "react";

import { Helmet } from "react-helmet";

import { Button, Checkbox } from "@alpacahq/ui";

import type { useAuthxAuthorizationPageController } from "./AuthxAuthorizationPage.controller";

type AuthxAuthorizationPagePresentationProps = ReturnType<
  typeof useAuthxAuthorizationPageController
>;

const ClientLink = ({
  clientName,
  clientUrl,
}: {
  clientName: string;
  clientUrl?: string;
}) => {
  if (!clientUrl) {
    return <>{clientName}</>;
  }

  return (
    <a
      href={clientUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-inherit underline"
    >
      {clientName}
    </a>
  );
};

const StatusCard = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col items-center justify-center sm:p-8">
    <Helmet title="Authorize" />
    <div className="relative rounded-lg border-gray-300 p-2 sm:mx-auto sm:max-w-lg sm:border sm:p-8 sm:shadow-sm">
      <div className="flex flex-col items-start space-y-8">
        <h1 className="font-semibold text-2xl text-black">{title}</h1>
        <span className="text-black text-md leading-5">{children}</span>
      </div>
    </div>
  </div>
);

export const AuthxAuthorizationPagePresentation = ({
  accountOptions,
  accountTypeLabel,
  clientName,
  clientUrl,
  errorMessage,
  isAllowDisabled,
  isLoading,
  isSubmitting,
  onAllow,
  onDeny,
  onSelectAccount,
  product,
  redirectUrl,
  requestedAccessLabel,
  requestedAccessTypeLabel,
}: AuthxAuthorizationPagePresentationProps) => {
  const clientLinkProps = { clientName, clientUrl };

  if (errorMessage) {
    return (
      <StatusCard title="An error has occurred">{errorMessage}</StatusCard>
    );
  }

  if (redirectUrl) {
    return (
      <StatusCard title="Redirecting you to the application">
        If the application hasn&apos;t opened,{" "}
        <a
          href={redirectUrl}
          className="text-inherit underline"
          aria-label="Open the application again"
        >
          click here
        </a>{" "}
        to try again.
      </StatusCard>
    );
  }

  if (!requestedAccessLabel || !product) {
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-center sm:p-8">
      <Helmet title="Authorize" />
      <div className="relative rounded-lg border-gray-300 p-2 sm:max-w-xl sm:border sm:p-8 sm:shadow-sm">
        <div className="flex flex-col items-start gap-4">
          <h1 className="font-semibold text-2xl">Authorize {clientName}</h1>
          <div>
            By allowing <ClientLink {...clientLinkProps} /> to access your
            Alpaca account, you are granting <ClientLink {...clientLinkProps} />{" "}
            access to your account information and authorization to place
            transactions at your direction.
          </div>
          <div>
            Alpaca does not warrant or guarantee that{" "}
            <ClientLink {...clientLinkProps} /> will work as advertised or
            expected. Before authorizing, learn more about{" "}
            <ClientLink {...clientLinkProps} />.
          </div>
          <div>
            <span className="font-semibold">Requested access: </span>
            {requestedAccessLabel} {requestedAccessTypeLabel}
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="font-semibold text-xl">
              Select an Alpaca {accountTypeLabel} to authorize:
            </h2>
            {isLoading && (
              <div className="text-gray-600 text-sm">Loading accounts...</div>
            )}
            {!isLoading && accountOptions.length === 0 && (
              <div>No {product} accounts are available.</div>
            )}
            {accountOptions.map((account) => (
              <div
                key={account.id}
                role="checkbox"
                aria-checked={account.isSelected}
                aria-label={account.label}
                tabIndex={0}
                onClick={() => onSelectAccount(account.id)}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    onSelectAccount(account.id);
                  }
                }}
                className="flex cursor-pointer items-center gap-4 rounded-lg border border-gray-300 p-2"
              >
                <Checkbox
                  checked={account.isSelected}
                  onClick={() => onSelectAccount(account.id)}
                  size="sm"
                />
                <span>
                  {account.label}{" "}
                  <span className="text-gray-400">{account.name}</span>
                </span>
              </div>
            ))}
          </div>

          <div className="flex flex-col text-gray-600 text-sm">
            <p className="font-semibold text-black">
              By tapping &ldquo;Allow&rdquo;, you agree to the following:
            </p>
            <p>
              You understand and agree that <ClientLink {...clientLinkProps} />{" "}
              is a third-party service and is not affiliated with Alpaca.
              Therefore, Alpaca cannot guarantee the availability, accuracy, or
              timeliness of services provided by{" "}
              <ClientLink {...clientLinkProps} />, including the correct and
              timely transmission of your trade orders. Alpaca is not
              responsible for any content or services provided by{" "}
              <ClientLink {...clientLinkProps} />, including any claims or
              opinions related to investing or the financial markets.
            </p>
            <p>
              Securities brokerage services are provided by Alpaca Securities
              LLC, member FINRA/SIPC, a wholly-owned subsidiary of AlpacaDB,
              Inc. Technology and services are offered by AlpacaDB, Inc. By
              clicking &ldquo;Allow,&rdquo; you acknowledge that Alpaca is not
              responsible for any of the services provided by{" "}
              <ClientLink {...clientLinkProps} />, including the trades that{" "}
              <ClientLink {...clientLinkProps} /> makes for your Alpaca
              brokerage account.
            </p>
          </div>
          <div className="flex w-full gap-4">
            <Button
              label="Deny"
              disabled={isSubmitting}
              onClick={onDeny}
              className="flex-1"
            />
            <Button
              label={isSubmitting ? "Authorizing..." : "Allow"}
              disabled={isAllowDisabled}
              onClick={onAllow}
              className="flex-1"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
